import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, fileUrl, avatarUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Send, Lock, Coins } from "lucide-react";
import { toast } from "sonner";

export default function Messages() {
  const { user, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const initialTo = params.get("to");
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(initialTo || null);
  const [activeUser, setActiveUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [threadLocked, setThreadLocked] = useState(false);
  const [hasInboundMessages, setHasInboundMessages] = useState(false);
  const [text, setText] = useState("");
  const [unlocking, setUnlocking] = useState(false);

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => { if (activeId) loadMessages(activeId); }, [activeId]);

  const free = user?.plan === "free" && user?.role !== "admin";
  const credits = Number(user?.interest_credits || 0);

  const loadThreads = async () => {
    try {
      const { data } = await api.get("/messages/threads");
      setThreads(data.results || []);
      if (initialTo) {
        const t = data.results?.find((t) => t.user.user_id === initialTo);
        if (t) setActiveUser(t.user);
        else {
          try {
            const p = await api.get(`/profiles/${initialTo}`);
            setActiveUser(p.data);
          } catch { /* ignore */ }
        }
      } else if (data.results?.length) {
        setActiveId(data.results[0].user.user_id);
        setActiveUser(data.results[0].user);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const loadMessages = async (id) => {
    try {
      const res = await api.get(`/messages/with/${id}`);
      const data = res.data;
      setMessages(data.results || []);
      setThreadLocked(!!data.locked);
      setHasInboundMessages(!!data.has_messages);
      const t = threads.find((x) => x.user.user_id === id);
      if (t) setActiveUser(t.user);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const unlockThread = async () => {
    if (!activeId) return;
    setUnlocking(true);
    try {
      await api.post(`/messages/unlock/${activeId}`);
      toast.success("Thread unlocked");
      await refresh();
      await loadMessages(activeId);
      await loadThreads();
    } catch (e) {
      const status = e.response?.status;
      const msg = formatApiError(e.response?.data?.detail);
      if (status === 402) {
        toast.error(msg, { action: { label: "Get credits", onClick: () => { window.location.href = "/plans"; } } });
      } else {
        toast.error(msg);
      }
    } finally {
      setUnlocking(false);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !activeId) return;
    try {
      await api.post("/messages/send", { to_user_id: activeId, text });
      setText("");
      await loadMessages(activeId);
      await loadThreads();
    } catch (err) {
      const status = err.response?.status;
      const msg = formatApiError(err.response?.data?.detail);
      if (status === 402) {
        toast.error(msg, { action: { label: "View plans", onClick: () => (window.location.href = "/plans") } });
      } else {
        toast.error(msg);
      }
    }
  };

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="messages-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <p className="overline">Conversations</p>
            <h1 className="font-serif text-4xl text-brand-ink mt-2">Messages</h1>
          </div>
          {free && (
            <div className="flex items-center gap-3 text-sm" data-testid="messages-credit-balance">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-muted border border-brand-line">
                <Coins className="h-3.5 w-3.5 text-brand-gold" /> {credits} credit{credits === 1 ? "" : "s"}
              </span>
              <Link to="/plans" className="text-brand-maroon hover:underline">Buy more</Link>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-12 gap-6 mt-8">
          <aside className="md:col-span-4 card-elegant p-2 max-h-[70vh] overflow-y-auto">
            {threads.length === 0 ? (
              <p className="text-sm text-brand-inkLight p-5">No conversations yet. Send an interest to start one.</p>
            ) : threads.map((t) => (
              <button
                key={t.user.user_id}
                onClick={() => { setActiveId(t.user.user_id); setActiveUser(t.user); setParams({ to: t.user.user_id }); }}
                className={`w-full text-left p-4 rounded-md flex items-center gap-3 ${activeId === t.user.user_id ? "bg-brand-muted" : "hover:bg-brand-muted/50"}`}
                data-testid={`thread-${t.user.user_id}`}
              >
                <div className="relative">
                  <img src={avatarUrl(t.user, 160)} alt="" className={`w-12 h-12 rounded-full object-cover ${t.locked ? "blur-[2px]" : ""}`} />
                  {t.locked && <Lock className="absolute -top-1 -right-1 h-4 w-4 text-brand-gold bg-white rounded-full p-0.5 border border-brand-line" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className={`font-serif text-base text-brand-ink truncate ${t.locked ? "blur-[2px] select-none" : ""}`}>{t.user.name}</p>
                    {t.unread > 0 && <span className="text-[10px] bg-brand-maroon text-white rounded-full px-2 py-0.5">{t.unread}</span>}
                  </div>
                  <p className={`text-xs truncate ${t.locked ? "text-brand-gold" : "text-brand-inkLight"}`}>{t.last_message?.text}</p>
                </div>
              </button>
            ))}
          </aside>

          <main className="md:col-span-8 card-elegant flex flex-col h-[70vh]">
            {activeUser ? (
              <>
                <div className="p-4 border-b border-brand-line flex items-center gap-3">
                  <img src={avatarUrl(activeUser, 160)} alt="" className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <p className="font-serif text-lg text-brand-ink">{activeUser.name}</p>
                    <p className="text-xs text-brand-inkLight">{activeUser.profession || ""}</p>
                  </div>
                </div>

                {free && threadLocked ? (
                  <div className="flex-1 flex flex-col items-center justify-center p-8 text-center" data-testid="thread-locked-card">
                    <div className="h-14 w-14 rounded-full bg-brand-muted flex items-center justify-center mb-4">
                      <Lock className="h-6 w-6 text-brand-gold" />
                    </div>
                    <p className="font-serif text-xl text-brand-ink">This conversation is locked</p>
                    <p className="text-sm text-brand-inkLight mt-2 max-w-md">
                      {hasInboundMessages
                        ? `${activeUser.name} has sent you messages. Spend 1 credit to read this thread and reply, or upgrade to Premium for unlimited chats.`
                        : `Unlock this thread for 1 credit to start chatting with ${activeUser.name}, or upgrade to Premium.`}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 mt-6">
                      <button
                        onClick={unlockThread}
                        disabled={unlocking || credits < 1}
                        className="btn-primary text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                        data-testid="unlock-thread-btn"
                      >
                        <Coins className="h-4 w-4" /> {unlocking ? "Unlocking..." : `Unlock — 1 credit`}
                      </button>
                      <Link to="/plans" className="btn-gold text-sm" data-testid="upgrade-from-thread">Upgrade to Premium</Link>
                    </div>
                    <p className="text-xs text-brand-inkMute mt-4">You have <span className="font-semibold text-brand-ink">{credits}</span> credit{credits === 1 ? "" : "s"}</p>
                  </div>
                ) : (
                  <>
                    <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="messages-list">
                      {messages.length === 0 ? (
                        <p className="text-center text-sm text-brand-inkMute py-10">No messages yet. Say hello.</p>
                      ) : messages.map((m) => (
                        <div key={m.id} className={`flex ${m.from_user_id === user.user_id ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl ${m.from_user_id === user.user_id ? "bg-brand-maroon text-white rounded-br-sm" : "bg-brand-muted text-brand-ink rounded-bl-sm"}`}>
                            <p className="text-sm leading-relaxed">{m.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <form onSubmit={send} className="p-3 border-t border-brand-line flex gap-2" data-testid="send-message-form">
                      <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message..." className="input-elegant flex-1" data-testid="message-input" />
                      <button type="submit" className="btn-primary px-5" data-testid="send-message-btn"><Send className="h-4 w-4" /></button>
                    </form>
                  </>
                )}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-brand-inkMute">Select a conversation</div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
